import type { VisitorFaceImage } from '../types/visitor';

export type VisitorFormValues = {
  visitorName: string;
  designation: string;
  companyName: string;
  phoneNumber: string;
  email: string;
  purposeOfVisit: string;
  consentGiven: boolean;
  faceImage: VisitorFaceImage | null;
};

export type VisitorFormErrors = Partial<
  Record<keyof VisitorFormValues, string>
>;

export const EMPTY_VISITOR_FORM: VisitorFormValues = {
  visitorName: '',
  designation: '',
  companyName: '',
  phoneNumber: '',
  email: '',
  purposeOfVisit: '',
  consentGiven: false,
  faceImage: null,
};

/** Same formats the API accepts (JPEG, PNG, BMP, GIF). */
export const VISITOR_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/bmp',
  'image/gif',
] as const;

export const VISITOR_IMAGE_MAX_BYTES = 6 * 1024 * 1024;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const NAME = /^[\p{L}][\p{L}\p{M} .'-]*$/u;
const PHONE = /^\+?[\d\s()-]+$/;

function nameError(value: string): string | undefined {
  const name = value.trim();
  if (!name) return 'Enter the visitor name.';
  if (name.length < 2 || name.length > 150 || !NAME.test(name)) {
    return 'Enter a valid name (letters only).';
  }
  return undefined;
}

function phoneError(value: string): string | undefined {
  const phone = value.trim();
  const digits = phone.replace(/\D/g, '');
  if (!phone) return 'Enter the phone number.';
  if (!PHONE.test(phone) || digits.length < 7 || digits.length > 15) {
    return 'Enter a valid phone number.';
  }
  return undefined;
}

/** Optional: checked only when filled in. */
function emailError(value: string): string | undefined {
  const email = value.trim();
  if (email && (email.length > 254 || !EMAIL.test(email))) {
    return 'Enter a valid email address.';
  }
  return undefined;
}

function withoutEmpty(errors: VisitorFormErrors): VisitorFormErrors {
  return Object.fromEntries(
    Object.entries(errors).filter(([, message]) => message),
  ) as VisitorFormErrors;
}

/** Mirrors the API validator so most mistakes are caught before upload. */
export function validateVisitorForm(values: VisitorFormValues): VisitorFormErrors {
  const errors: VisitorFormErrors = {
    visitorName: nameError(values.visitorName),
    phoneNumber: phoneError(values.phoneNumber),
    email: emailError(values.email),
  };

  if (values.designation.trim().length > 150) {
    errors.designation = 'Designation is too long.';
  }

  const company = values.companyName.trim();
  if (!company) errors.companyName = 'Enter the company name.';
  else if (company.length > 200) errors.companyName = 'Company name is too long.';

  const purpose = values.purposeOfVisit.trim();
  if (!purpose) errors.purposeOfVisit = 'Enter the purpose of the visit.';
  else if (purpose.length > 500) errors.purposeOfVisit = 'Purpose is too long.';

  if (!values.faceImage) errors.faceImage = 'Capture or upload the visitor’s face.';
  if (!values.consentGiven) {
    errors.consentGiven = 'The visitor must consent to face capture.';
  }
  return withoutEmpty(errors);
}

export type QuickVisitorFormValues = Omit<
  VisitorFormValues,
  'consentGiven' | 'faceImage'
>;

export const EMPTY_QUICK_VISITOR_FORM: QuickVisitorFormValues = {
  visitorName: '',
  designation: '',
  companyName: '',
  phoneNumber: '',
  email: '',
  purposeOfVisit: '',
};

/**
 * Kiosk quick registration (Mark Attendance, unmatched face): only name and
 * phone are required; no face image or consent. Optional fields are checked
 * only when filled in.
 */
export function validateQuickVisitorForm(
  values: QuickVisitorFormValues,
): VisitorFormErrors {
  const errors: VisitorFormErrors = {
    visitorName: nameError(values.visitorName),
    phoneNumber: phoneError(values.phoneNumber),
    email: emailError(values.email),
  };
  if (values.designation.trim().length > 150) {
    errors.designation = 'Designation is too long.';
  }
  if (values.companyName.trim().length > 200) {
    errors.companyName = 'Company name is too long.';
  }
  if (values.purposeOfVisit.trim().length > 500) {
    errors.purposeOfVisit = 'Purpose is too long.';
  }
  return withoutEmpty(errors);
}

/** Checks a library pick before upload; returns an error message or null. */
export function validatePickedImage(asset: {
  type?: string | null;
  fileSize?: number | null;
}): string | null {
  const type = (asset.type ?? '').toLowerCase();
  if (type && !(VISITOR_IMAGE_TYPES as readonly string[]).includes(type)) {
    return 'Use a JPEG, PNG, BMP or GIF photo.';
  }
  if (asset.fileSize != null && asset.fileSize > VISITOR_IMAGE_MAX_BYTES) {
    return 'The photo is larger than 6 MB.';
  }
  if (asset.fileSize != null && asset.fileSize < 1024) {
    return 'The photo is too small. Use a clearer image.';
  }
  return null;
}
